import { BusinessOutlined } from "@mui/icons-material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import type { AgencyWage } from "../../../utils/models";
import { type AgencyCellFormat, type AgencyDisplayColumn, agencyCellText, agencyDisplayColumns, agencyRowsOf } from "../../team/AgencyWages/agencyWageColumns";
import { ConfigRowsPreview, type ConfigRowsPreviewColumn, type ConfigRowsPreviewRow } from "../../UIToolkit/Tables/ConfigTable/ConfigRowsPreview";
import { useBreakdownPanel } from "../BreakdownPanelContext";
import { moneyOrHidden } from "../consts";
import { toCents } from "../wageComposition";
import { useRuleCardAgencyWages } from "./useRuleCardReads";

const AGENCY_COLUMN_ID = "agency";
const TEST_ID = "wage-breakdown-card-agency";

/** Agency names are typed by hand on both sides; one written with other capitals is still that agency. */
const sameAgency = (a: string, b: string): boolean => a.trim().toLowerCase() === b.trim().toLowerCase();

/** The agency's row as the grid shows it — Agency, the T&T terms and bill hours, then a rate per role; `[]` when it has none. */
const agencyPreviewRows = (
	wages: readonly AgencyWage[],
	agencyName: string,
	displayColumns: readonly AgencyDisplayColumn[],
	format: AgencyCellFormat,
): ConfigRowsPreviewRow[] => {
	const agency = agencyRowsOf(wages).find((row) => sameAgency(row.agencyName, agencyName));
	if (agency === undefined) return [];
	const cells = Object.fromEntries(
		displayColumns.map((column) => [
			column.backend,
			// DEFAULT: a role the agency has no rate for is a blank cell, as in the grid.
			agencyCellText(column, agency.roleWages[column.backend]?.wage ?? null, format),
		]),
	);
	return [{ id: agency.agencyName, cells: { [AGENCY_COLUMN_ID]: agency.agencyName, ...cells } }];
};

/**
 * The shift's agency's row from Team → Agency Configuration, today's rates, read through that grid's own
 * cache entry, with its columns and formatting — rates through the panel's hide rule.
 */
const LiveAgencySection: FC<{ agencyName: string }> = ({ agencyName }) => {
	const { t } = useTranslation();
	const { chosenDepartment, departmentNurseTypes } = useHospitalData();
	const { canViewWage } = useBreakdownPanel();
	const wages = useRuleCardAgencyWages(chosenDepartment.department_id);
	const displayColumns = agencyDisplayColumns(departmentNurseTypes);
	const columns: ConfigRowsPreviewColumn[] = [
		{ id: AGENCY_COLUMN_ID, header: t("agency") },
		...displayColumns.map((column) => ({ id: column.backend, header: column.display })),
	];
	const shared = { title: t("agency.configuration"), icon: <BusinessOutlined />, columns, dataTestId: TEST_ID };
	if (wages.isPending) return <ConfigRowsPreview {...shared} rows={[]} loadingLabel={t("wageBreakdown.card.loading")} />;
	if (wages.isError) return <ConfigRowsPreview {...shared} rows={[]} messages={[t("wageBreakdown.card.agencyLoadError")]} />;
	const format: AgencyCellFormat = {
		wage: (wage) => moneyOrHidden(toCents(wage), canViewWage),
		hours: (hours) => t("agencyWages.hoursValue", { hours }),
	};
	const rows = agencyPreviewRows(wages.data, agencyName, displayColumns, format);
	const messages = rows.length > 0 ? [] : [t("wageBreakdown.card.agencyMissing", { agency: agencyName })];
	return <ConfigRowsPreview {...shared} rows={rows} messages={messages} />;
};

/**
 * The agency part of an agency shift's card. It reads Agency Configuration, so it shows only to a viewer
 * who could open that tab (the same gate as "Agency rates for this department"); for anyone else the card
 * has no agency part — no row was captured to show instead. Without a recorded agency there is no row to
 * look for.
 */
export const AgencyRuleSection: FC = () => {
	const { t } = useTranslation();
	const { agencyName, access } = useBreakdownPanel();
	if (!access.canOpenAgencyConfiguration) return null;
	if (agencyName !== null) return <LiveAgencySection agencyName={agencyName} />;
	return (
		<ConfigRowsPreview
			title={t("agency.configuration")}
			icon={<BusinessOutlined />}
			columns={[]}
			rows={[]}
			messages={[t("wageBreakdown.card.agencyUnknown")]}
			dataTestId={TEST_ID}
		/>
	);
};
